import { Add, Search } from '@mui/icons-material';
import { Alert, Button, CircularProgress, Grid, IconButton, InputAdornment, TextField } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Container, Form } from 'react-bootstrap';
import { getProjectIssues } from '../../../../modules/jira';
import { IssueSearchTableHeaders } from '../../../project-dashboard/test-case/testCaseTableHead';
import Modal from '../../Modal';
import TableComponent from '../../TableComponent';
import JiraIssueLinkage from './JiraIssueLinkage';
import LinkedProjectSelector from './LinkedProjectSelector';

const buildJQL = (input, projectKey, linkedProjectId) => {
  const trimmed = typeof input === 'string' ? input.trim() : '';
  if (!trimmed) return `project = ${linkedProjectId}`;

  const keyRegex = /^[A-Za-z][A-Za-z0-9]+-[0-9]+$/;
  const digitsOnly = /^\d+$/;

  if (digitsOnly.test(trimmed)) {
    return `project = ${linkedProjectId} AND key = "${projectKey}-${trimmed}"`;
  }

  if (keyRegex.test(trimmed)) {
    return `project = ${linkedProjectId} AND key = "${trimmed}"`;
  }

  return `project = ${linkedProjectId} AND summary ~ "${trimmed}*"`;
};

const transformIssues = (issues, jiraSite,projectId) =>
  issues.map((issue) => ({
    id: issue.id,
    key: issue.key,
    issuestatus:
      issue.fields.status.status_category?.name ||
      issue.fields.status.name,
    issueType: issue.fields.issuetype.name,
    issueTypeIcon: issue.fields.issuetype.icon_url,
    summary: issue.fields.summary,
    reporter: issue.fields.reporter?.display_name || '',
    priority: issue.fields.priority?.name || '',
    priority_url: issue.fields.priority?.icon_url || '',
    labels: issue.fields.labels || [],   
    siteUrl: jiraSite,
    projectId: projectId,
  }));

const SearchIssuesModalComponent = ({
  show,
  setShowSearchModal,
  handleAddIssues,
  linkedIssueIds,
  currentProject,
  step,
  issueKey,
  testCaseData,
  setStep,
  submitting,
  setSubmitting,
  linkedJiraProjects,
  handleShowCreateModal,
 setSelectedProjectFromSearch = () => {},

}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [issues, setIssues] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [err, setErr] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [persistedSelection, setPersistedSelection] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const hasLinkedProject = linkedJiraProjects?.length > 0;
  const handleSearch = useCallback(
    async (term, project) => {
      if (!project) return;

      setIsLoading(true);
      setErr('');
      try {
        const projectKey = project.linked_project_key;
        const jql = buildJQL(term, projectKey, project.linked_project_id);

        const body = {
          fields: ['summary', 'status', 'assignee', 'reporter', 'issuetype', 'priority','labels'],
          jql,
        };

        const response = await getProjectIssues(body, currentProject?.id);

        if (Array.isArray(response?.issues)) {
          setIssues(transformIssues(response.issues, currentProject?.jira_site_url,project.linked_project_id));
        } else {
          setIssues([]);
        }
      } catch (e) {
        setErr('Error fetching issues. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [currentProject]
  );

  useEffect(() => {
    if (!show) return;

    if (!hasLinkedProject) {
      setIssues([]);
      return;
    }
    const first = selectedProject || linkedJiraProjects[0];
    setSelectedProject(first);
    handleSearch('', first);
  }, [show]);

  useEffect(() => {
    const inCurrentSearch = issues.filter(i => selectedIds.includes(i.id));
    const stored = persistedSelection.filter(
      p => selectedIds.includes(p.id) && !issues.some(i => i.id === p.id)
    );

    const merged = [...inCurrentSearch, ...stored];
    const unique = Array.from(new Map(merged.map(i => [i.id, i])).values());

    setSelectedRows(unique);
  }, [issues, selectedIds, persistedSelection]);
useEffect(() => {
  if (!show) {
    setSelectedIds([]);
    setSelectedRows([]);
    setPersistedSelection([]);
  }
}, [show]);
  const handleSelectChange = (ids) => {
    setSelectedIds(ids);

    const selectedFromSearch = issues.filter(i => ids.includes(i.id));

    setPersistedSelection(prev => {
      const filteredPrev = prev.filter(p => ids.includes(p.id));
      const merged = [...filteredPrev, ...selectedFromSearch];
      const unique = Array.from(new Map(merged.map(x => [x.id, x])).values());
      return unique;
    });
  };

  const isRowDisabled = useCallback(
    (row) => linkedIssueIds.includes(row.id),
    [linkedIssueIds]
  );

  const handleClose = () => {
    if (submitting) return;
    setSearchTerm('');
    setIssues([]);
    setSelectedIds([]);
    setSelectedRows([]);
    setPersistedSelection([]);
    setErr('');
    setSelectedProject(null);
    setShowSearchModal(false);
    setStep(1);
  };

  const handleSubmitSearch = (e) => {
    e.preventDefault();
    handleSearch(searchTerm, selectedProject);
  };

  const handleAdd = () => {
    if (selectedRows.length > 10) {
      setErr('You can only link a maximum of 10 issues at a time.');
      return;
    }
    handleAddIssues(selectedRows);
  };

  const handleProjectChange = (proj) => {
    setSelectedProject(proj);
    handleSearch(searchTerm, proj);
  };
   const handleSearchTermChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
        if (value === '' ) {
      handleSearch('', selectedProject);
    }
  };
   const handleCreateNewIssueClick = () => {
    if (typeof setSelectedProjectFromSearch === 'function') {
      setSelectedProjectFromSearch(selectedProject);
    }
    handleShowCreateModal();
  };
  return (
    <Modal
      open={show}
      handleClose={submitting ? undefined : handleClose}
      modalTitle={step === 1 ? 'Search Issues' : 'Create Issue Links'}
      maxWidth="lg"
      size="xl"
      action={() => (
        <>
          <Button
            title="Close"
            variant="outlined"
            color="secondary"
            disabled={submitting}
            onClick={handleClose}>
            Close
          </Button>

          {step === 1 && (
            <Button
              variant="contained"
              onClick={handleAdd}
              disabled={submitting || selectedIds.length === 0}>
              {submitting ? 'Adding...' : 'Add'}
            </Button>
          )}
        </>
      )}>
      <Container fluid>
        {!hasLinkedProject ? (
          <Alert severity="warning" className="mt-3">
            No external project is linked with this project. Please link a project first.
          </Alert>
        ) : step === 1 ? (
          <>
            <Form onSubmit={handleSubmitSearch}>
              <Grid sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 2 }}>
                <Grid item xs={3}>
                  <LinkedProjectSelector
                    linkedProjects={linkedJiraProjects}
                    onProjectChange={handleProjectChange}
                    selectedProjectId={selectedProject?.linked_project_id}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    placeholder="Search issue key, ID, or summary"
                    value={searchTerm}
                    onChange={handleSearchTermChange}
                    fullWidth
                    size="small"
                    sx={{ border: 'none !important' }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleSubmitSearch}
                            disabled={isLoading || submitting}
                            edge="end"
                            size="small"
                            sx={{
                              border: '1px solid',
                            }}
                          >
                            {isLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Search size={18} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid>
                  <Button
                    variant="contained"
                    onClick={handleCreateNewIssueClick}
                    startIcon={<Add />}
                    disabled={isLoading || submitting}>
                    Create New Issue
                  </Button>
                </Grid>
              </Grid>

              {selectedRows.length > 0 && (
                <div className="mt-3">
                  <strong>Selected Issues: </strong>
                  {selectedRows.map((r) => r.key).join(', ')}
                </div>
              )}
            </Form>

            {err && (
              <Alert severity="error" className="my-2">
                {err}
              </Alert>
            )}

            <div style={{ maxHeight: 300, overflowY: 'auto', marginTop: '1rem' }}>
              <TableComponent
                data={issues}
                headerTabs={IssueSearchTableHeaders}
                isLoading={isLoading}
                showCheckbox
                selected={selectedIds}
                setSelected={handleSelectChange}
                isRowDisabled={isRowDisabled}
              />
            </div>
          </>
        ) : (
          <JiraIssueLinkage
            testCaseId={testCaseData?.id}
            issueKey={issueKey}
            projectId={currentProject?.id}
            handleModalClose={handleClose}
            type="search"
            setSubmitting={setSubmitting}
          />
        )}
      </Container>
    </Modal>
  );
};

export default SearchIssuesModalComponent;
